/**
 * `React.lazy` for a page, surviving a deploy (issue 953).
 *
 * A tab opened before a deploy still runs the old bundle, which names its
 * chunks by the old content hashes. The first visit to a lazy page after the
 * deploy asks for a chunk the server no longer has, and nginx answers any
 * missing path with the SPA's index.html (200, text/html). Webpack rejects
 * the import with a ChunkLoadError, and the error boundary replaces the page.
 * A refresh fetches the new bundle, which is why "refreshing gets you in".
 *
 * So a failed chunk load reloads the page once, at the address the reader was
 * going to. The reload is remembered for RELOAD_WINDOW_MS in sessionStorage:
 * a second failure inside it is real (the network, or a broken build) and is
 * thrown to the error boundary rather than reloading forever.
 */
import { lazy } from 'react';

export const RELOAD_KEY = 'tdt-chunk-reload';
export const RELOAD_WINDOW_MS = 30000;

export function isChunkLoadError(err) {
  if (!err) return false;
  if (err.name === 'ChunkLoadError') return true;
  return /Loading (CSS )?chunk [\w-]+ failed|dynamically imported module|Unexpected token '<'/i
    .test(String(err.message || ''));
}

function recentlyReloaded(now) {
  try {
    const at = Number(window.sessionStorage.getItem(RELOAD_KEY));
    return Number.isFinite(at) && at > 0 && now - at < RELOAD_WINDOW_MS;
  } catch (e) {
    // Storage blocked: without a record there is no loop guard, so do not reload.
    return true;
  }
}

export function importOrReload(factory, reload = () => window.location.reload()) {
  return factory().catch((err) => {
    const now = Date.now();
    if (!isChunkLoadError(err) || recentlyReloaded(now)) throw err;
    try { window.sessionStorage.setItem(RELOAD_KEY, String(now)); } catch (e) { throw err; }
    reload();
    // Keep the Suspense fallback up until the reload takes the page away.
    return new Promise(() => {});
  });
}

export default function lazyPage(factory) {
  return lazy(() => importOrReload(factory));
}
